// Pure derivation of the topics nav from a window's stored position and the
// user's flat topic list. `level` + `path` (ancestor ids, root first) define
// the tree the same way an item's level and path do.

function idOf(value) {
  if (value == null) return ''
  if (typeof value === 'object') return String(value._id || '')
  return String(value)
}

function immediateParentId(topic) {
  const path = topic?.path || []
  if (!path.length) return ''
  return idOf(path[path.length - 1])
}

function byName(a, b) {
  return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
}

function childrenMap(topics) {
  const byId = new Map(topics.filter((topic) => topic?._id).map((topic) => [String(topic._id), topic]))
  const map = new Map()
  topics.forEach((topic) => {
    if (!topic?._id) return
    const key = immediateParentId(topic)
    if (!key) {
      if ((topic.level || 0) !== 0) return
    } else {
      const parent = byId.get(key)
      if (!parent || (topic.level || 0) !== (parent.level || 0) + 1) return
    }
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(topic)
  })
  map.forEach((list) => list.sort(byName))
  return map
}

// Depth-first descendants of `rootId` ('' is the forest). A folded topic stays
// in the list; its subtree does not. `keep`, when set, drops a topic and its
// subtree when the topic itself is not kept.
function visibleDescendants(map, rootId, collapsedSet, keep) {
  const rows = []
  const walk = (id) => {
    const kids = map.get(id) || []
    kids.forEach((child) => {
      if (keep && !keep(child)) return
      rows.push(child)
      if (!collapsedSet.has(String(child._id))) walk(String(child._id))
    })
  }
  walk(rootId || '')
  return rows
}

export function deriveTopicView({ nav, topics, matches }) {
  const list = Array.isArray(topics) ? topics : []
  const mode = nav?.mode === 'collapsed' ? 'collapsed' : 'segmented'
  const byId = new Map(list.filter((topic) => topic?._id).map((topic) => [String(topic._id), topic]))

  // Stop when a drilled topic is no longer a child of the one before it, which
  // happens after that topic is moved. A missing topic (list still loading)
  // does not count, so a saved trail is not thrown away before topics arrive.
  const trail = []
  let trailBroken = false
  let expectedParent = ''
  for (const id of nav?.trailIds || []) {
    const topic = byId.get(String(id))
    if (!topic) break
    if (immediateParentId(topic) !== expectedParent) {
      trailBroken = true
      break
    }
    trail.push(topic)
    expectedParent = String(topic._id)
  }
  const current = trail.length ? trail[trail.length - 1] : null
  const atRoot = !current
  const collapsedSet = new Set((nav?.collapsed || []).map(String))
  const children = childrenMap(list)
  const rootKey = current ? String(current._id) : ''

  const scope = visibleDescendants(children, rootKey, new Set())

  // Search keeps a hit and the parents between it and the current drill.
  // Parents that are not themselves hits are dimmed. Other branches drop out.
  let matchSet = null
  let ancestorSet = null
  if (typeof matches === 'function') {
    matchSet = new Set()
    ancestorSet = new Set()
    scope.forEach((topic) => {
      if (!topic?._id || !matches(topic)) return
      matchSet.add(String(topic._id))
      ;(topic.path || []).forEach((id) => {
        const key = idOf(id)
        if (key) ancestorSet.add(key)
      })
    })
  }
  const keep = (topic) => {
    if (!matchSet) return true
    const id = String(topic._id)
    return matchSet.has(id) || ancestorSet.has(id)
  }

  const rows =
    mode === 'collapsed'
      ? visibleDescendants(children, rootKey, collapsedSet, matchSet ? keep : null)
      : (children.get(rootKey) || []).filter(keep)

  const hasChildren = (topic) => (children.get(String(topic?._id)) || []).length > 0
  const isDimmed = (topic) => Boolean(matchSet) && !matchSet.has(String(topic?._id))

  return {
    atRoot,
    mode,
    trail,
    rows,
    scope,
    current,
    collapsedSet,
    hasChildren,
    filterActive: Boolean(matchSet),
    isDimmed,
    trailBroken,
  }
}
